import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || t('forgotPassword.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
        <Card className="max-w-md w-full p-8 text-center" data-testid="forgot-password-sent">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-3">{t('forgotPassword.sentTitle')}</h1>
          <p className="text-gray-600 mb-2">
            {t('forgotPassword.sentBody1')} <strong>{email}</strong> {t('forgotPassword.sentBody2')}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t('forgotPassword.sentHint')}
          </p>
          <Link to="/login">
            <Button variant="outline" className="w-full" data-testid="forgot-back-login">
              <ArrowLeft className="h-4 w-4 mr-2" />{t('forgotPassword.backToLogin')}
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <Card className="max-w-md w-full p-8">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold">{t('forgotPassword.title')}</h1>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          {t('forgotPassword.subtitle')}
        </p>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription data-testid="forgot-error">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="fp-email">{t('forgotPassword.emailLabel')}</Label>
            <Input
              id="fp-email"
              type="email"
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('forgotPassword.emailPlaceholder')}
              required
              data-testid="forgot-email-input"
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={submitting}
            data-testid="forgot-submit-btn"
          >
            {submitting ? t('forgotPassword.sending') : t('forgotPassword.submit')}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-gray-500 hover:text-blue-600 underline">
            <ArrowLeft className="h-3 w-3 inline mr-1" />{t('forgotPassword.backToLogin')}
          </Link>
        </div>
      </Card>
    </div>
  );
}
